import { useNavigate } from "@solidjs/router";
import { createSignal, For, Show } from "solid-js";
import {
  PASSES,
  passView,
  publicTariff,
  type Tab,
  TARIF_BLEU,
  VELOTAF_CENTS,
} from "../../shared/tariffs";
import { AppBar } from "../components/AppBar";
import { ComingSoonToast, comingSoon } from "../components/ComingSoon";
import { OperatedBy } from "../components/OperatedBy";
import { ScreenLoader } from "../components/Spinner";
import { pricingPlans } from "../state/pricing";
import { session } from "../state/session";

const euros = (cents: number) => `${(cents / 100).toFixed(2).replace(".", ",")} €`;

/**
 * Figma A0 "Tarifs et pass vélo" (66:9683): tabs Tous publics (the GBFS e-bike plan) / Tarif bleu
 * (hardcoded, decision 14/10/2026), the violet price card, the passes and Vélotaf. Passes can't be
 * bought yet (no API): display only, a tap says so. Opens on Tarif bleu once the service is
 * activated (designer note).
 */
export function Prices() {
  const navigate = useNavigate();
  // Back to where the rider came from (the map, with the selected bike), else the map.
  const back = () => (history.length > 1 ? navigate(-1) : navigate("/map"));
  const [tab, setTab] = createSignal<Tab>(session.me()?.activated ? "bleu" : "public");
  const pub = () => publicTariff(pricingPlans());
  const rate = () => (tab() === "bleu" ? TARIF_BLEU.perMinuteCents : pub()?.perMinuteCents);
  const soon = () => comingSoon("Achat de pass bientôt disponible");

  return (
    <main class="screen bg-surface">
      <AppBar title="Tarifs et pass vélo" back={back} />
      {/* Figma tabs-standard: two halves, violet label and 104 × 3 indicator on the active one. */}
      <div class="relative mx-[15px] flex h-[58px] shrink-0 border-b border-border" role="tablist">
        <For
          each={
            [
              ["public", "Tous publics"],
              ["bleu", "Tarif bleu"],
            ] as const
          }
        >
          {([id, label]) => (
            <button
              type="button"
              role="tab"
              aria-selected={tab() === id}
              class={`relative flex-1 text-[19px] font-medium leading-[1.25] tracking-[0.19px] ${tab() === id ? "text-violet" : "text-strong"}`}
              onClick={() => setTab(id)}
            >
              {label}
              <Show when={tab() === id}>
                <span class="absolute bottom-0 left-1/2 h-[3px] w-[104px] -translate-x-1/2 rounded-t-[3px] bg-violet" />
              </Show>
            </button>
          )}
        </For>
      </div>
      <Show
        when={tab() === "bleu" || pub() || !pricingPlans.loading}
        fallback={<ScreenLoader label="Chargement des tarifs…" />}
      >
        <div class="screen-body px-[15px] pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {/* Hero card: violet, the outline bike in the background. */}
          <div class="relative h-[140px] shrink-0 overflow-hidden rounded-[20px] bg-violet px-5 text-white">
            <img
              src="/img/bike-hero-170.svg"
              width="170"
              height="170"
              alt=""
              class="absolute left-[222px] top-[30px]"
            />
            <span class="absolute left-5 top-[14px] flex h-6 items-center rounded-[12px] bg-white/18 px-3 text-xs font-medium leading-[1.25]">
              {tab() === "bleu" ? "Tarif bleu · résidents et actifs" : "Tous publics"}
            </span>
            {/* Figma puts it top-right, which only fits 402 px wide: on the price line, it fits phones
                from 375 px. */}
            <Show when={tab() === "bleu"}>
              <button
                type="button"
                class="absolute right-3 top-[50px] flex h-8 items-center rounded-[16px] bg-white px-3 text-[13px] font-medium text-violet"
                onClick={() => comingSoon()}
              >
                Mon éligibilité
              </button>
            </Show>
            <Show
              when={rate() !== undefined}
              fallback={
                <p class="absolute left-5 top-[52px] text-base font-medium">
                  Tarifs indisponibles pour le moment.
                </p>
              }
            >
              <div class="absolute left-5 top-[38px] flex items-baseline gap-[6px]">
                <span class="text-[46px] font-semibold leading-[1.1]">
                  {euros(rate() as number)}
                </span>
                <span class="text-lg font-medium opacity-85">/ min</span>
              </div>
              <p class="absolute left-5 top-[94px] text-base font-medium leading-[1.25]">
                {tab() === "bleu"
                  ? "Déverrouillage offert"
                  : (pub()?.unlockCents ?? 0) > 0
                    ? `Déverrouillage : ${euros(pub()?.unlockCents ?? 0)}`
                    : "Déverrouillage offert"}
              </p>
              <p class="absolute left-5 top-[118px] text-[11.5px] leading-[1.25] opacity-85">
                {tab() === "bleu"
                  ? "Justificatif : domicile, employeur ou FMD"
                  : "Sans justificatif"}
              </p>
            </Show>
          </div>

          <h2 class="mt-[10px] px-[5px] text-[19px] font-semibold leading-[1.25] tracking-[0.19px] text-strong">
            Pass et abonnements
          </h2>
          <p class="mt-[2px] px-[5px] text-[13px] leading-[1.25] tracking-[0.13px] text-text">
            Roulez plus, payez moins chaque minute.
          </p>
          <div class="mt-[10px] grid grid-cols-2 gap-3">
            <For each={PASSES}>
              {(p) => {
                const v = () => passView(p, tab(), rate() ?? Number.POSITIVE_INFINITY);
                return (
                  <button
                    type="button"
                    class={`relative h-[148px] rounded-[16px] bg-white px-4 pt-3 text-left shadow-[0_3px_8px_rgba(51,26,77,0.08)] ${v().best ? "border-2 border-violet" : "border border-border"}`}
                    onClick={soon}
                  >
                    <span class="flex items-baseline gap-1 text-violet">
                      <span class="text-[34px] font-semibold leading-[1.1]">{p.minutes}</span>
                      <span class="text-sm font-medium">min</span>
                    </span>
                    <Show when={rate() !== undefined && v().discount}>
                      {(d) => (
                        <span class="absolute right-3 top-4 flex h-[22px] items-center rounded-[11px] bg-green px-2 text-xs font-semibold text-white">
                          {d()}
                        </span>
                      )}
                    </Show>
                    <span class="mt-[6px] block text-sm font-medium leading-[1.25] text-strong">
                      {p.name}
                    </span>
                    <span class="block text-[22px] font-semibold leading-[1.25] text-strong">
                      {v().price}
                    </span>
                    <span class="block text-xs leading-[1.25] text-text">{v().perMinute}</span>
                    <Show when={v().best}>
                      <span class="block text-[11px] font-semibold leading-[1.25] text-violet">
                        Meilleur prix à la minute
                      </span>
                    </Show>
                  </button>
                );
              }}
            </For>
          </div>

          {/* Vélotaf: tarif bleu only; from Tous publics it leads there. */}
          <button
            type="button"
            class="mt-3 flex min-h-[84px] w-full items-center gap-[14px] rounded-[16px] border border-border bg-white px-4 py-[14px] text-left"
            onClick={() => (tab() === "public" ? setTab("bleu") : soon())}
          >
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo">
              <img src="/icons/bike-white-36.svg" width="24" height="24" alt="" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-base font-semibold leading-[1.25] text-strong">Vélotaf</span>
              <span class="mt-1 block text-xs leading-[1.25] text-text">
                {tab() === "bleu"
                  ? "2 trajets de 20 min par jour, idéal domicile-travail"
                  : "Réservé au tarif bleu, sur justificatif"}
              </span>
            </span>
            <span class="shrink-0 text-lg font-semibold text-strong">
              {tab() === "bleu" ? euros(VELOTAF_CENTS) : "Tarif bleu"}
            </span>
            <img src="/icons/chevron-right.svg" width="22" height="22" alt="" class="shrink-0" />
          </button>

          <p class="mx-auto mt-4 max-w-[342px] text-center text-[11px] leading-[1.25] text-text">
            Économies calculées par rapport au tarif à la minute, si toutes les minutes du pass sont
            utilisées.
          </p>
          <OperatedBy class="mt-auto pt-4" />
        </div>
      </Show>
      <ComingSoonToast />
    </main>
  );
}
